import { t } from 'i18next';
import { CompressorParameter, StationParameter } from './common';

export const BASE_PAGE_INDEX = 0;

export const SetpointsConfiguration = 'setpointsConfiguration';

export enum EngineStepsEnum {
  CompressorGroups = 'compressorGroups',
  SensorsConfiguration = 'sensorsConfiguration',
  MainRangesConfiguration = 'mainRangesConfiguration',
  DefaultSetpointsConfiguration = 'defaultSetpointsConfiguration',
  StationConfiguration = 'stationConfiguration',
  CompressorParametersConfiguration = 'compressorsConfiguration',
}

export enum EngineSubstepsEnum {
  FlowLevels = 'flowLevels',
  PressureLevels = 'pressureLevels',
  MainRangesResult = 'mainRangesResult',
  FlowSensorsPriority = 'flowSensorsPriority',
  PressureSensorsPriority = 'pressureSensorsPriority',
  SensorsPriorityResult = 'sensorsPriorityResult',
  CompressorsGroups = 'compressorsGroups',
  DefaultSetpoints = 'defaultSetpoints',
  Setpoints = 'setpoints',
  FallbackScenarios = 'fallbackScenarios',
  StationConfiguration = 'stationConfiguration',
  CompressorParametersConfiguration = 'compressorParametersConfiguration',
  SensorsKPIConfiguration = 'sensorsKPIConfiguration',
}

export enum ValidationTriggerFromStepEnum {
  EngineStep = 'engineStep',
}

export enum EngineStatusName {
  Active = 'active',
  ActiveFallback = 'activeFallback',
  Inactive = 'inactive',
  Disabled = 'disabled',
  Maintenance = 'maintenance',
  Unavailable = 'unavailable',
}

export const subStepsWithValidations = [
  EngineSubstepsEnum.FlowLevels,
  EngineSubstepsEnum.PressureLevels,
  EngineSubstepsEnum.FlowSensorsPriority,
  EngineSubstepsEnum.PressureSensorsPriority,
  EngineSubstepsEnum.CompressorsGroups,
  EngineSubstepsEnum.FallbackScenarios,
  EngineSubstepsEnum.DefaultSetpoints,
];

export const subStepsWithoutBackButton = [
  EngineSubstepsEnum.FlowLevels,
  EngineSubstepsEnum.FlowSensorsPriority,
  EngineSubstepsEnum.CompressorsGroups,
  EngineSubstepsEnum.DefaultSetpoints,
  EngineSubstepsEnum.StationConfiguration,
  EngineSubstepsEnum.CompressorParametersConfiguration,
];

export enum SensorsLogicEnum {
  Average = 'average',
  Sum = 'sum',
}

export enum SensorsAvailabilityEnum {
  All = 'all',
  Any = 'any',
}

export enum DefaultBehaviour {
  ExistingRule = 'existing-rule',
  CustomRule = 'custom-rule',
  TurnOffEngine = 'turn-off-engine',
}

export enum FallbackBehaviour {
  TurnOffEngine = 'turn-off-engine',
  SingleRuleForAllFlowLevels = 'single-rule-for-all-flow-levels',
  LogicPerFlowLevel = 'logic-per-flow-level',
  FlowPressureKPIMissing = 'missing-flow-pressure-kpi',
  FlowKPIMissing = 'missing-flow-kpi',
  PressureKPIMissing = 'missing-pressure-kpi',
}

export enum CompressorPriority {
  Active = 'Active',
  Backup = 'Backup',
  Ignored = 'Ignored',
}

export enum EngineConfigStatus {
  Draft = 'draft',
  Published = 'published',
  Wizard = 'wizard',
  Recommendation = 'recommendation',
  DefaultStrategy = 'defaultStrategy',
}

export enum EngineUpdatedFrom {
  Draft = 'manual',
  Recommendation = 'algo',
}

export enum RejectionReason {
  ProbabilityOfReducingStationPressureBelowMinimum = 'probabilityofreducingstationpressurebelowminimum',
  ProbabilityOfReducingSavingBelowCommitment = 'probabilityofreducingsavingsbelowthecommitment',
  ProbabilityOfHarmingCompressorsHealth = 'probabilityofharmingcompressorshealth',
  ProbabilityOfIncreasingStationPressure = 'probabilityofincreasingstationpressure',
  EdgeCaseProperlyNotCovered = 'edgecasenotproperlycovered',
  Other = 'other',
}

export const RejectReasonLabels: Record<RejectionReason, string> = {
  [RejectionReason.ProbabilityOfReducingStationPressureBelowMinimum]: `${t(
    'probabilityofreducingstationpressurebelowminimum',
  )}`,
  [RejectionReason.ProbabilityOfReducingSavingBelowCommitment]: `${t(
    'probabilityofreducingsavingsbelowthecommitment',
  )}`,
  [RejectionReason.ProbabilityOfHarmingCompressorsHealth]: `${t('probabilityofharmingcompressorshealth')}`,
  [RejectionReason.ProbabilityOfIncreasingStationPressure]: `${t('probabilityofincreasingstationpressure')}`,
  [RejectionReason.EdgeCaseProperlyNotCovered]: `${t('edgecasenotproperlycovered')}`,
  [RejectionReason.Other]: `${t('Other')}`,
};

export enum RejectionCause {
  Flowlevels = 'flowlevels',
  PressureRanges = 'pressureranges',
  WrongCompressorCombinations = 'wrongcompressorcombinations',
  PressureDrop = 'pressuredrop',
  SetpointsForCompressor = 'setpointsforcompressor',
  FallbackCoverage = 'fallbackcoverage',
  Other = 'other',
}

export const RejectionCauseLabels: Record<RejectionCause, string> = {
  [RejectionCause.Flowlevels]: `${t('flowlevels')}`,
  [RejectionCause.PressureRanges]: `${t('Pressure Ranges')}`,
  [RejectionCause.WrongCompressorCombinations]: `${t('wrongcompressorcombinations')}`,
  [RejectionCause.PressureDrop]: `${t('pressuredrop')}`,
  [RejectionCause.SetpointsForCompressor]: `${t('setpointsforcompressor')}`,
  [RejectionCause.FallbackCoverage]: `${t('fallbackcoverage')}`,
  [RejectionCause.Other]: `${t('Other')}`,
};

export interface ChangeItem {
  deviceId: string;
  deviceName: string;
}

export interface PIDChanges {
  compressorsAdded: ChangeItem[];
  compressorsRemoved: ChangeItem[];
  flowSensorsRemoved: ChangeItem[];
  pressureSensorsRemoved: ChangeItem[];
  compressorPriorityChangedToIgnore: ChangeItem[];
  compressorPriorityChangedFromIgnore: ChangeItem[];
}

export interface BandSetpoints {
  minimumPressureBand1Bar: number;
  maximumPressureBand1Bar: number;
  minimumPressureBand2Bar: number;
  maximumPressureBand2Bar: number;
}

export interface EngineMetricSetpoints {
  metricType: string;
  deviceId: string;
  value: number;
}

export interface EngineConfigAdditionalInfo {
  fallback?: {
    ignoreFallbackCompressors?: string[];
  };
  approvalReportUrl?: string;
  recommendationWorkflowInfo?: {
    isViewedConfig?: boolean;
    isViewedReport?: boolean;
  };
}

export interface RawEngineConfig {
  additionalInfo?: EngineConfigAdditionalInfo;
  ignoreFallbackCompressors?: string[];
  fallbackScenarios?: {
    ignoreFallbackCompressors?: string[];
    [key: string]: unknown;
  };
  version?: string;
  configurationStatus?: EngineConfigStatus;
  [key: string]: unknown;
}

export const steps = [
  EngineStepsEnum.SensorsConfiguration,
  EngineStepsEnum.MainRangesConfiguration,
  EngineStepsEnum.DefaultSetpointsConfiguration,
  SetpointsConfiguration,
  EngineSubstepsEnum.FallbackScenarios,
  EngineStepsEnum.CompressorGroups,
];

export const centrifugalSteps = [
  EngineStepsEnum.StationConfiguration,
  EngineStepsEnum.CompressorParametersConfiguration,
  EngineStepsEnum.SensorsConfiguration,
];

export const engineReadOnlyVersions = [EngineConfigStatus.Published, EngineConfigStatus.Recommendation];

export const ruleEditableFields = [
  'name',
  'load',
  'order',
  'unload',
  'autoStop',
  'immediateStop',
  'vsdTarget',
  'modulate',
  'bandConfiguration',
  'pressureDrop',
];

export const stationParametersEditableFields: (keyof StationParameter)[] = [
  'stationTargetPressure',
  'normalPressureOffsetFromTarget',
  'reloadPressure',
  'startPressure',
  'emergencyStartPressure',
  'minAllowedActiveCompressors',
  'unloadingTestPressureFactor',
  'minAllowedUnloadTestingAttemptRateMinutes',
  'unloadCommandMinTargetSafetyPercentage',
  'minAllowedStartTimeIntervalMinutes',
  'minAllowedRestartTimeIntervalMinutes',
  'unloadTestingFlowPeakBuffer',
  'minIntervalFromHighFlowTrigger',
  'unloadTestingCommandFrequencySeconds',
  'kpiTimeIntervalMinutes',
  'loadBalancingPressureFactor',
  'emergencyStartPriorityRotationDelayMinutes',
  'bovAvgUpperBoundThresholdPercentage',
  'compressorBovUpperBoundThresholdPercentage',
  'maxLoadBuffer',
  'unloadTestingFlowBufferPercentage',
];

export const compressorEditableFields: (keyof CompressorParameter)[] = [
  'priority',
  'maxTarget',
  'minTarget',
  'stopDelayMinutes',
];

export const rangeEditableFields = ['lowerBound', 'upperBound'];
export const compressorGroupsEditableFields = ['name', 'rotation', 'repeatEvery', 'repeatOn', 'time', 'compressors'];
export const scenarioGroupsEditableFields = ['name', 'compressors', 'fallbackBehaviour', 'fallbackRules'];

export const ruleNumericFields = ['order', 'load', 'unload', 'autoStop', 'immediateStop', 'vsdTarget', 'pressureDrop'];
export const numericFields = ['lowerBound', 'upperBound', ...ruleNumericFields];

export enum SetpointsMappping {
  order = 'order',
  load = 'load',
  unload = 'unload',
  auto = 'auto',
  immediate = 'immediate',
  target = 'target',
  modulation = 'modulation',
  band = 'band',
  pressureDrop = 'pressureDrop',
}

export const ENGINE_VERSION_BASE = '1.5.';
export const ENGINE_VERSION_BASE_MIN = '1';
export const ENGINE_VERSION_1_0 = '1.0';
export const ENGINE_VERSION_1_5 = '1.5';

export interface CompressorParameterSave {
  deviceId: string;
  minTarget: number;
  maxTarget: number;
  stopDelayMinutes: number;
  priority: number;
}

export class StatusDto {
  id: number;
  name: string;
  baselineInDays?: number;
  order: number;
}
